import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { LocalizedLink } from '@/components/LocalizedLink';

import { Cookie, X, Settings, Shield, BarChart3, Megaphone } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Switch } from '@/components/ui/switch';
import { trackPageView } from '@/lib/analytics';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';

const COOKIE_CONSENT_KEY = 'i-rent-cookie-consent';
const COOKIE_PREFERENCES_KEY = 'i-rent-cookie-preferences';

// Custom event for opening cookie settings from anywhere
export const OPEN_COOKIE_SETTINGS_EVENT = 'open-cookie-settings';

export interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
}

const defaultPreferences: CookiePreferences = {
  essential: true,
  analytics: false,
  marketing: false,
};

type ConsentStatus = 'pending' | 'accepted' | 'rejected' | 'custom';

export function CookieConsent() {
  const { t } = useLanguage();
  const [status, setStatus] = useState<ConsentStatus>('pending');
  const [isVisible, setIsVisible] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>(defaultPreferences);

  useEffect(() => {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (stored === 'accepted' || stored === 'rejected' || stored === 'custom') {
      setStatus(stored as ConsentStatus);
      const storedPrefs = localStorage.getItem(COOKIE_PREFERENCES_KEY);
      if (storedPrefs) {
        try {
          setPreferences(JSON.parse(storedPrefs));
        } catch {
          // ignore parse errors
        }
      }
    } else {
      // Delay cookie banner to after LCP (3+ seconds) to avoid blocking Speed Index
      const timer = setTimeout(() => setIsVisible(true), 3500);
      return () => clearTimeout(timer);
    }
  }, []);

  // Listen for external open event
  useEffect(() => {
    const handleOpenFromExternal = () => {
      // Load current preferences from localStorage
      const storedPrefs = localStorage.getItem(COOKIE_PREFERENCES_KEY);
      if (storedPrefs) {
        try {
          setPreferences(JSON.parse(storedPrefs));
        } catch {
          setPreferences(defaultPreferences);
        }
      }
      setIsSettingsOpen(true);
    };

    window.addEventListener(OPEN_COOKIE_SETTINGS_EVENT, handleOpenFromExternal);
    return () => window.removeEventListener(OPEN_COOKIE_SETTINGS_EVENT, handleOpenFromExternal);
  }, []);

  const loadGoogleAnalytics = () => {
    if (typeof window !== 'undefined' && !document.querySelector('script[src*="googletagmanager"]')) {
      const s = document.createElement('script');
      s.async = true;
      s.src = 'https://www.googletagmanager.com/gtag/js?id=G-35ZTZE920Z';
      document.head.appendChild(s);
      const w = window as any;
      w.dataLayer = w.dataLayer || [];
      // Expose gtag globally so app code (page views, events) can queue hits.
      w.gtag = w.gtag || function gtag(...args: any[]) { w.dataLayer.push(args); };
      const gtag = w.gtag;
      gtag('js', new Date());
      gtag('config', 'G-35ZTZE920Z', {
        send_page_view: false,
        client_storage: 'none',
        anonymize_ip: true,
        ads_data_redirection: false,
        allow_google_signals: false,
        allow_ad_personalization_signals: false,
      });
      // GA was enabled mid-session: record the current page immediately.
      trackPageView({ path: window.location.pathname + window.location.search });
    }
  };


  const handleAcceptAll = () => {
    const allAccepted: CookiePreferences = {
      essential: true,
      analytics: true,
      marketing: true,
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, 'accepted');
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(allAccepted));
    setPreferences(allAccepted);
    setStatus('accepted');
    setIsVisible(false);
    setIsSettingsOpen(false);
    loadGoogleAnalytics();
  };

  const handleRejectAll = () => {
    const rejected: CookiePreferences = {
      essential: true,
      analytics: false,
      marketing: false,
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, 'rejected');
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(rejected));
    setPreferences(rejected);
    setStatus('rejected');
    setIsVisible(false);
    setIsSettingsOpen(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'custom');
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(preferences));
    setStatus('custom');
    setIsVisible(false);
    setIsSettingsOpen(false);
    if (preferences.analytics) {
      loadGoogleAnalytics();
    }
  };

  const handleOpenSettings = () => {
    setIsSettingsOpen(true);
  };

  const showBanner = status === 'pending' && isVisible && !isSettingsOpen;

  return (
    <>
      <AnimatePresence>
        {showBanner && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6"
          >
            <div className="container max-w-4xl mx-auto">
              <div className="bg-card border shadow-lg rounded-xl p-4 md:p-6 relative">
                <div className="flex flex-col md:flex-row gap-4 md:items-center">
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <Cookie className="w-5 h-5 text-primary" />
                    </div>
                  </div>
                  
                  <div className="flex-1 space-y-2">
                    <h3 className="font-semibold text-foreground">
                      {t('cookie.title')}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {t('cookie.description')}{' '}
                      <LocalizedLink routeKey="privacy" className="text-primary hover:underline">
                        {t('cookie.learnMore')}
                      </LocalizedLink>

                    </p>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-2 md:flex-shrink-0">
                    <Button variant="ghost" size="sm" onClick={handleOpenSettings} className="w-full sm:w-auto">
                      <Settings className="w-4 h-4 mr-2" />
                      {t('cookie.settings')}
                    </Button>
                    <Button variant="outline" onClick={handleRejectAll} className="w-full sm:w-auto">
                      {t('cookie.reject')}
                    </Button>
                    <Button onClick={handleAcceptAll} className="w-full sm:w-auto">
                      {t('cookie.accept')}
                    </Button>
                  </div>

                  <button
                    onClick={handleRejectAll}
                    className="absolute top-2 right-2 md:hidden p-1 text-muted-foreground hover:text-foreground transition-colors"
                    aria-label="Close"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <Dialog open={isSettingsOpen} onOpenChange={setIsSettingsOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Cookie className="w-5 h-5 text-primary" />
              {t('cookie.settingsTitle')}
            </DialogTitle>
            <DialogDescription>
              {t('cookie.settingsDescription')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Essential Cookies */}
            <div className="flex items-start justify-between gap-4 p-4 rounded-lg bg-muted/50">
              <div className="flex gap-3">
                <Shield className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <div className="space-y-1">
                  <h4 className="font-medium text-sm">{t('cookie.essential')}</h4>
                  <p className="text-xs text-muted-foreground">
                    {t('cookie.essentialDescription')}
                  </p>
                </div>
              </div>
              <Switch checked disabled aria-label="Essential cookies (always enabled)" />
            </div>

            {/* Analytics Cookies */}
            <div className="flex items-start justify-between gap-4 p-4 rounded-lg border">
              <div className="flex gap-3">
                <BarChart3 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <div className="space-y-1">
                  <h4 className="font-medium text-sm">{t('cookie.analytics')}</h4>
                  <p className="text-xs text-muted-foreground">
                    {t('cookie.analyticsDescription')}
                  </p>
                </div>
              </div>
              <Switch
                checked={preferences.analytics}
                onCheckedChange={(checked) => setPreferences({ ...preferences, analytics: checked })}
                aria-label="Analytics cookies"
              />
            </div>

            {/* Marketing Cookies */}
            <div className="flex items-start justify-between gap-4 p-4 rounded-lg border">
              <div className="flex gap-3">
                <Megaphone className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <div className="space-y-1">
                  <h4 className="font-medium text-sm">{t('cookie.marketing')}</h4>
                  <p className="text-xs text-muted-foreground">
                    {t('cookie.marketingDescription')}
                  </p>
                </div>
              </div>
              <Switch
                checked={preferences.marketing}
                onCheckedChange={(checked) => setPreferences({ ...preferences, marketing: checked })}
                aria-label="Marketing cookies"
              />
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={handleRejectAll} className="w-full sm:w-auto">
              {t('cookie.rejectAll')}
            </Button>
            <Button variant="outline" onClick={handleAcceptAll} className="w-full sm:w-auto">
              {t('cookie.acceptAll')}
            </Button>
            <Button onClick={handleSavePreferences} className="w-full sm:w-auto">
              {t('cookie.savePreferences')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
